// Sleeping tyrannosaurs (server-owned state). Procedural low-poly rex with
// three poses: sleeping (crouched, Zzz), waking (rising), roaring (jaw open,
// expanding shockwave ring). The roar blast itself is applied in main.js.
import {
  MeshBuilder, StandardMaterial, Color3, TransformNode, Vector3, Mesh,
  DynamicTexture,
} from '@babylonjs/core';
import { REXES, rexBlastRFor } from '../shared/level.mjs';

function mat(scene, name, hex, emissive = 0.12) {
  const m = new StandardMaterial(name, scene);
  m.diffuseColor = Color3.FromHexString(hex);
  m.emissiveColor = m.diffuseColor.scale(emissive);
  m.specularColor = new Color3(0.04, 0.04, 0.04);
  return m;
}

function starTexturePlane(scene, size = 1.6) {
  const dt = new DynamicTexture('star', { width: 128, height: 128 }, scene, true);
  const c = dt.getContext();
  c.clearRect(0, 0, 128, 128);
  c.font = 'bold 100px Georgia'; c.textAlign = 'center'; c.textBaseline = 'middle';
  c.lineWidth = 8; c.strokeStyle = '#7a5b1e'; c.strokeText('★', 64, 70);
  c.fillStyle = '#ffd75e'; c.fillText('★', 64, 70);
  dt.update(); dt.hasAlpha = true;
  const p = MeshBuilder.CreatePlane('starP', { size }, scene);
  const m = new StandardMaterial('starM', scene);
  m.diffuseTexture = dt; m.useAlphaFromDiffuseTexture = true;
  m.emissiveColor = new Color3(1, 1, 1); m.specularColor = Color3.Black();
  m.backFaceCulling = false;
  p.material = m;
  p.billboardMode = Mesh.BILLBOARDMODE_ALL;
  return p;
}

function zzzMesh(scene) {
  const dt = new DynamicTexture('zzz', { width: 128, height: 128 }, scene, true);
  const c = dt.getContext();
  c.clearRect(0, 0, 128, 128);
  c.font = 'bold 96px Georgia'; c.textAlign = 'center'; c.textBaseline = 'middle';
  c.lineWidth = 10; c.strokeStyle = '#2d3a2a'; c.strokeText('Z', 64, 68);
  c.fillStyle = '#f5ecd8'; c.fillText('Z', 64, 68);
  dt.update(); dt.hasAlpha = true;
  const p = MeshBuilder.CreatePlane('zzzP', { size: 3.4 }, scene);
  const m = new StandardMaterial('zzzM', scene);
  m.diffuseTexture = dt; m.useAlphaFromDiffuseTexture = true;
  m.emissiveColor = new Color3(1, 1, 1); m.specularColor = Color3.Black();
  m.backFaceCulling = false;
  p.material = m;
  p.billboardMode = Mesh.BILLBOARDMODE_ALL;
  return p;
}

function buildRexMesh(scene, def) {
  const root = new TransformNode('rex' + def.id, scene);
  root.position.set(def.x, def.y, def.z);
  root.rotation.y = def.face;
  root.scaling.setAll(1.35); // king-size — palms are 15 m, the rex should loom

  const body = new TransformNode('rexBody' + def.id, scene); // rises when waking
  body.parent = root;

  const green = mat(scene, 'rex_green', '#5e7d3a');
  const belly = mat(scene, 'rex_belly', '#a8b06a');
  const dark = mat(scene, 'rex_dark', '#41582a');
  const mouth = mat(scene, 'rex_mouth', '#8f3d33', 0.2);
  const tooth = mat(scene, 'rex_tooth', '#f2ecd9', 0.3);

  // torso: fat tilted capsule
  const torso = MeshBuilder.CreateCapsule('rx_torso', { radius: 2.1, height: 8.2, tessellation: 8, subdivisions: 1, capSubdivisions: 3 }, scene);
  torso.rotation.x = Math.PI / 2 - 0.35; torso.position.set(0, 4.4, -0.4);
  torso.material = green; torso.parent = body;
  const bellyM = MeshBuilder.CreateCapsule('rx_belly', { radius: 1.7, height: 6, tessellation: 8, subdivisions: 1, capSubdivisions: 3 }, scene);
  bellyM.rotation.x = Math.PI / 2 - 0.35; bellyM.position.set(0, 3.7, 0);
  bellyM.material = belly; bellyM.parent = body;

  // tail: two tapered segments
  const tail1 = MeshBuilder.CreateCylinder('rx_tail1', { height: 5, diameterTop: 1.1, diameterBottom: 2.9, tessellation: 7 }, scene);
  tail1.rotation.x = -Math.PI / 2 + 0.45; tail1.position.set(0, 3.4, -5.6);
  tail1.material = green; tail1.parent = body;
  const tail2 = MeshBuilder.CreateCylinder('rx_tail2', { height: 4.4, diameterTop: 0.25, diameterBottom: 1.1, tessellation: 6 }, scene);
  tail2.rotation.x = -Math.PI / 2 + 0.7; tail2.position.set(0, 2.9, -9.4);
  tail2.material = dark; tail2.parent = body;

  // legs + feet
  for (const s of [-1, 1]) {
    const thigh = MeshBuilder.CreateCapsule('rx_thigh', { radius: 1.15, height: 4.6, tessellation: 7 }, scene);
    thigh.position.set(s * 1.9, 2.9, -1.4); thigh.rotation.z = -s * 0.15;
    thigh.material = green; thigh.parent = body;
    const shin = MeshBuilder.CreateCylinder('rx_shin', { height: 2.6, diameter: 1.0, tessellation: 6 }, scene);
    shin.position.set(s * 2.2, 1.2, -1.2); shin.material = dark; shin.parent = body;
    const foot = MeshBuilder.CreateBox('rx_foot', { width: 1.6, height: 0.8, depth: 2.6 }, scene);
    foot.position.set(s * 2.2, 0.4, -0.6); foot.material = dark; foot.parent = body;
  }
  // tiny arms
  for (const s of [-1, 1]) {
    const arm = MeshBuilder.CreateCapsule('rx_arm', { radius: 0.35, height: 1.9, tessellation: 6 }, scene);
    arm.position.set(s * 1.9, 4.9, 2.6); arm.rotation.x = 1.0;
    arm.material = green; arm.parent = body;
  }

  // neck + head with hinged jaw
  const neck = MeshBuilder.CreateCylinder('rx_neck', { height: 2.8, diameterTop: 2.0, diameterBottom: 2.7, tessellation: 7 }, scene);
  neck.position.set(0, 6.6, 2.6); neck.rotation.x = 0.5;
  neck.material = green; neck.parent = body;

  const head = new TransformNode('rexHead' + def.id, scene);
  head.parent = body; head.position.set(0, 8.0, 3.6);

  const skull = MeshBuilder.CreateBox('rx_skull', { width: 2.5, height: 1.9, depth: 3.6 }, scene);
  skull.position.set(0, 0.5, 1.0); skull.material = green; skull.parent = head;
  const snout = MeshBuilder.CreateBox('rx_snout', { width: 1.9, height: 1.2, depth: 2.2 }, scene);
  snout.position.set(0, 0.15, 3.4); snout.material = dark; snout.parent = head;
  // brow ridges
  for (const s of [-1, 1]) {
    const brow = MeshBuilder.CreateBox('rx_brow', { width: 0.7, height: 0.5, depth: 1.1 }, scene);
    brow.position.set(s * 0.85, 1.55, 1.7); brow.material = dark; brow.parent = head;
  }
  // upper teeth strip
  const teethU = MeshBuilder.CreateBox('rx_teethU', { width: 1.7, height: 0.35, depth: 2.0 }, scene);
  teethU.position.set(0, -0.45, 3.3); teethU.material = tooth; teethU.parent = head;

  // jaw pivots at the back so rotation.x opens the mouth
  const jaw = new TransformNode('rexJaw' + def.id, scene);
  jaw.parent = head; jaw.position.set(0, -0.7, 0.4);
  const jawM = MeshBuilder.CreateBox('rx_jaw', { width: 2.0, height: 0.8, depth: 4.6 }, scene);
  jawM.position.set(0, -0.4, 2.1); jawM.material = mouth; jawM.parent = jaw;
  const teethL = MeshBuilder.CreateBox('rx_teethL', { width: 1.6, height: 0.3, depth: 1.8 }, scene);
  teethL.position.set(0, 0.05, 3.2); teethL.material = tooth; teethL.parent = jaw;

  // Zzz above the snoozing head
  const zzz = zzzMesh(scene);
  zzz.parent = root; zzz.position.set(0, 7.5, 3.5);

  // circling stars for the stunned state
  const stars = new TransformNode('rexStars' + def.id, scene);
  stars.parent = root; stars.position.set(0, 7.6, 3.2);
  for (let i = 0; i < 3; i++) {
    const st = starTexturePlane(scene);
    st.parent = stars;
    const a = (i / 3) * Math.PI * 2;
    st.position.set(Math.cos(a) * 2.6, 0, Math.sin(a) * 2.6);
  }
  stars.setEnabled(false);

  // Shared danger ring: 'waking' drives it as a static blast-radius warning,
  // 'roaring' as the expanding shockwave. ringOwner says who is holding it.
  const ring = MeshBuilder.CreateTorus('rx_ring', { diameter: 6, thickness: 0.7, tessellation: 24 }, scene);
  const ringM = new StandardMaterial('rx_ringM', scene);
  ringM.diffuseColor = Color3.FromHexString('#fff2c8');
  ringM.emissiveColor = Color3.FromHexString('#ffe9a0');
  ringM.alpha = 0.5; ringM.specularColor = Color3.Black();
  ring.material = ringM;
  ring.parent = root; ring.position.y = 6;
  ring.isPickable = false;
  ring.setEnabled(false);

  return { root, body, head, jaw, zzz, stars, ring, ringM };
}

export class Rexes {
  constructor(scene) {
    this.scene = scene;
    this.list = REXES.map(def => ({
      def,
      mesh: buildRexMesh(scene, def),
      state: 'sleeping',   // sleeping | waking | roaring | stunned
      animT: 0,            // seconds since last state change
      pose: 0,             // 0 = crouched asleep … 1 = upright
      ringOwner: null,     // 'waking' | 'roaring' — who drives the shared ring
    }));
  }

  byId(id) { return this.list.find(r => r.def.id === id); }

  setState(id, state) {
    const r = this.byId(id);
    if (!r || r.state === state) return;
    r.state = state;
    r.animT = 0;
    // reset the shared ring, then hand it to whoever needs it now
    const m = r.mesh;
    m.ring.scaling.setAll(1);
    m.ring.position.y = 6;
    m.ringM.alpha = 0.5;
    m.ringM.emissiveColor = Color3.FromHexString('#ffe9a0');
    m.ringM.diffuseColor = Color3.FromHexString('#fff2c8');
    r.ringOwner = (state === 'waking' || state === 'roaring') ? state : null;
    m.ring.setEnabled(r.ringOwner !== null);
    m.stars.setEnabled(state === 'stunned');
  }

  // heliPos and levelN are optional: the offline tools call update(t, dt).
  update(t, dt, heliPos = null, levelN = 1) {
    const blastR = rexBlastRFor(levelN);
    for (const r of this.list) {
      r.animT += dt;
      const m = r.mesh;
      // pose blending: crouched (0) ↔ upright (1); stunned collapses fast
      const target = (r.state === 'sleeping' || r.state === 'stunned') ? 0 : 1;
      r.pose += (target - r.pose) * Math.min(1, dt * (r.state === 'waking' ? 4 : r.state === 'stunned' ? 6 : 2));
      m.body.position.y = -2.6 * (1 - r.pose);
      m.body.rotation.x = 0.35 * (1 - r.pose);

      if (r.state === 'sleeping') {
        // slow breathing + bobbing Zzz
        const breathe = 1 + Math.sin(t * 1.1 + r.def.id * 2) * 0.03;
        m.body.scaling.set(1, breathe, 1);
        m.jaw.rotation.x = 0.06 + Math.sin(t * 1.1 + r.def.id * 2) * 0.03;
        // the head must be lerped back down: roaring/stunned leave it tilted,
        // and nothing else ever writes it again once he falls asleep
        const hk = Math.min(1, dt * 2);
        const rest = 0.12 + Math.sin(t * 1.1 + r.def.id * 2) * 0.04;
        m.head.rotation.x += (rest - m.head.rotation.x) * hk;
        m.head.rotation.z += (0 - m.head.rotation.z) * hk;
        m.zzz.setEnabled(true);
        const zt = (t * 0.5 + r.def.id * 0.3) % 1;
        m.zzz.position.y = 5.2 + zt * 4;
        m.zzz.scaling.setAll(0.5 + zt * 0.9);
        m.zzz.visibility = 1 - zt * 0.85;
      } else if (r.state === 'stunned') {
        m.body.scaling.set(1, 1, 1);
        m.zzz.setEnabled(false);
        m.jaw.rotation.x = 0.5;               // slack jaw, tongue-out daze
        m.head.rotation.x = 0.25;
        m.stars.rotation.y = t * 3;           // stars orbiting the head
      } else {
        m.body.scaling.set(1, 1, 1);
        m.zzz.setEnabled(false);
        if (r.state === 'waking') {
          m.jaw.rotation.x = 0.1 + r.animT * 0.15; // mouth slowly opening…
          m.head.rotation.x = -0.15 * r.pose;
          // TELEGRAPH: paint the blast reach on the ground during the warning
          // window, so the roar is something you can fly out of, not a toast.
          if (r.ringOwner === 'waking') {
            // torus radius is 3 in local units and the whole rex is scaled 1.35;
            // Y is squashed so the fat tube reads as a flat band on the ground
            const sc = blastR / (3 * 1.35);
            m.ring.scaling.set(sc, sc * 0.10, sc);
            m.ring.position.y = 4;
            const pulse = 0.5 + 0.5 * Math.sin(t * 7 + r.def.id);
            // hotter and brighter when the player is actually inside the zone
            const inside = heliPos
              && Math.hypot(heliPos.x - r.def.x, heliPos.z - r.def.z) < blastR ? 1 : 0;
            m.ringM.alpha = 0.16 + 0.14 * pulse + 0.12 * inside;
            m.ringM.emissiveColor = Color3.Lerp(
              Color3.FromHexString('#ff9a3c'), Color3.FromHexString('#ff3b20'),
              Math.min(1, pulse * 0.6 + inside * 0.4));
          }
        } else { // roaring
          m.jaw.rotation.x = 0.85 + Math.sin(t * 18) * 0.06; // wide open, quivering
          m.head.rotation.x = -0.35;
          // expanding shockwave ring
          const rt = r.animT % 1.4;
          const scale = 1 + rt * 14;
          m.ring.setEnabled(r.animT < 4.5);
          m.ring.scaling.set(scale, scale, scale);
          m.ringM.alpha = Math.max(0, 0.45 - rt * 0.35);
        }
      }
    }
  }
}
